import { useState } from 'react';

import { DialogProps } from '@cast/design-system';
import { Cluster } from '@cast/types';

import { AssignCredentialsDialogV2 } from 'hooks/dialogs/cluster/useAssignCredentialsDialogV2/AssignCredentialsDialogV2';
import { FeatureName, FeatureSelection } from 'hooks/dialogs/cluster/types';
import { notReachable } from 'utils/types/not-reachable';

import { CastctlOnboardClusterDialog } from './CastctlOnboardingDialog';
import { CASTCTL_SUPPORTED_FEATURES } from './CastctlOnboardingDialog/constants';
import { CastctlFeatureSelection } from './CastctlOnboardingDialog/types';

type DialogVariant = 'castctl' | 'script';

export type ConnectClusterDialogProps = Omit<DialogProps, 'onClose'> & {
  cluster?: Cluster;
  promotedFeature?: FeatureName;
  skipConnectionMonitoring?: boolean;
  initialFeatureSelection?: Partial<CastctlFeatureSelection>;
  shouldNavigateOnSuccess?: boolean;
  onSuccess?: (features: FeatureSelection) => void;
  onClose: () => void;
  onCompleted?: () => void;
};

const getCastctlInitialFeatureSelection = (
  promotedFeature?: FeatureName,
  initialFeatureSelection?: Partial<CastctlFeatureSelection>
): Partial<CastctlFeatureSelection> | undefined => {
  if (
    !promotedFeature ||
    !CASTCTL_SUPPORTED_FEATURES.some((feature) => feature === promotedFeature)
  ) {
    return initialFeatureSelection;
  }

  return {
    [promotedFeature]: true,
    ...initialFeatureSelection,
  };
};

export const ConnectClusterDialog = ({
  cluster,
  promotedFeature,
  skipConnectionMonitoring,
  initialFeatureSelection,
  shouldNavigateOnSuccess,
  onSuccess,
  onClose,
  onCompleted,
  ...dialogProps
}: ConnectClusterDialogProps) => {
  const [variant, setVariant] = useState<DialogVariant>('castctl');

  const castctlInitialFeatureSelection = getCastctlInitialFeatureSelection(
    promotedFeature,
    initialFeatureSelection
  );

  switch (variant) {
    case 'castctl':
      return (
        <CastctlOnboardClusterDialog
          {...dialogProps}
          skipConnectionMonitoring={skipConnectionMonitoring}
          initialFeatureSelection={castctlInitialFeatureSelection}
          onClose={onClose}
          onCompleted={onCompleted ?? onClose}
          onSwitchToLegacyScript={
            cluster ? () => setVariant('script') : undefined
          }
        />
      );
    case 'script':
      if (!cluster) {
        return null;
      }

      return (
        <AssignCredentialsDialogV2
          cluster={cluster}
          destroy={onClose}
          promotedFeature={promotedFeature}
          shouldNavigateOnSuccess={shouldNavigateOnSuccess}
          onSuccess={onSuccess}
        />
      );
    default:
      return notReachable(variant);
  }
};
